import type { ManualChapter } from './types'

export const introductionChapter: ManualChapter = {
  id: 'introduction',
  number: 1,
  title: 'Introduzione',
  description: 'Panoramica completa della piattaforma Community',
  icon: 'BookOpenIcon',
  sections: [
    {
      id: 'what-is',
      title: "Cos'è la Community Platform",
      requirement: '§1 Obiettivi del Progetto',
      content: `
## Cos'è {{APP_NAME}} Community Platform

**{{APP_NAME}} Community Platform** è un ecosistema digitale moderno, collaborativo e professionale progettato per supportare le esigenze delle community aziendali.

Non è un semplice sito web, un forum o un corso online. È **tutto questo insieme**, integrato in un'unica esperienza che:

- **Attrae** professionisti del settore con contenuti di valore
- **Forma** attraverso percorsi strutturati e certificazioni riconosciute
- **Coinvolge** con meccaniche di gioco che premiano la partecipazione
- **Fidelizza** creando un senso di appartenenza e riconoscimento

### Obiettivi della Piattaforma

La piattaforma risponde ai seguenti obiettivi:

| Obiettivo | Descrizione |
|-----------|-------------|
| **Condivisione** | Scambio di conoscenze e best practice tra membri |
| **Formazione** | Percorsi certificati e aggiornamento continuo |
| **Collaborazione** | Lavoro strutturato in Task Force tematiche |
| **Documentazione** | Repository con strumenti operativi sempre disponibili |
| **Engagement** | Gamification avanzata per partecipazione attiva |
| **Servizi** | Consulenza e supporto dedicati ai membri |
`
    },
    {
      id: 'target-audience',
      title: 'A Chi è Destinata',
      content: `
## A Chi è Destinata

La piattaforma è progettata per diversi tipi di utenti:

### Membri della Community
Professionisti che desiderano:
- Accedere a formazione di qualità
- Confrontarsi con colleghi del settore
- Utilizzare strumenti operativi validati
- Partecipare a gruppi di lavoro tematici

### Manager e Responsabili
Figure che necessitano di:
- Monitorare la formazione del proprio team
- Accedere a dashboard con KPI rilevanti
- Coordinare Task Force operative

### Amministratori
Personale tecnico responsabile di:
- Gestire utenti e permessi
- Configurare corsi e contenuti
- Moderare la community
- Analizzare statistiche di utilizzo

### Esperti e Formatori
Professionisti che:
- Creano contenuti formativi
- Rispondono a quesiti tecnici
- Validano documenti e risposte
- Conducono eventi e webinar
`
    },
    {
      id: 'use-cases',
      title: "Scenari d'Uso Tipici",
      requirement: 'Offerta §4 "Il Viaggio dell\'Utente"',
      content: `
## Scenari d'Uso Tipici

### Scenario 1: Il Nuovo Membro

**Mario** è un professionista che si iscrive alla community per la prima volta.

1. **Registrazione**: Compila il form e attende l'approvazione dell'amministratore
2. **Primo accesso**: Visualizza la dashboard con suggerimenti personalizzati
3. **Formazione base**: Completa il corso introduttivo e ottiene il primo badge
4. **Partecipazione**: Inizia a seguire le discussioni nel forum
5. **Riconoscimento**: Sale nella classifica dei nuovi membri

*Tempo stimato: prima settimana di utilizzo*

---

### Scenario 2: Formazione Avanzata

**Laura** decide di approfondire le proprie competenze con un percorso strutturato.

1. **Iscrizione al Mini-Master**: Seleziona il percorso dal catalogo corsi
2. **Studio progressivo**: Completa i 12 moduli video con quiz intermedi
3. **Esame finale**: Supera la verifica con punteggio minimo
4. **Certificazione**: Scarica il certificato ufficiale in PDF
5. **Riconoscimento**: Ottiene badge speciale e punti bonus

*Tempo stimato: 8-12 settimane*

---

### Scenario 3: Collaborazione in Task Force

**Marco** viene invitato a partecipare a un gruppo di lavoro tematico.

1. **Accesso alla Task Force**: Riceve invito e accetta la partecipazione
2. **Orientamento**: Visualizza la Kanban board con i task assegnati
3. **Comunicazione**: Utilizza la chat real-time con il team
4. **Produzione**: Collabora alla stesura di documenti condivisi
5. **Consegna**: Il deliverable viene approvato e pubblicato

*Tempo stimato: durata progetto (variabile)*

---

### Scenario 4: Consulenza Compliance

**Anna** ha un dubbio tecnico su una questione normativa.

1. **Accesso alla Compliance Clinic**: Naviga alla sezione dedicata
2. **Nuova domanda**: Compila il form descrivendo il quesito
3. **Smistamento**: Il sistema assegna automaticamente un esperto
4. **Risposta**: Riceve notifica quando la risposta è pronta
5. **Archiviazione**: La risposta resta consultabile per riferimento futuro

*Tempo stimato: 24-72 ore per risposta*

---

### Scenario 5: Gamification e Competizione

**Paolo** vuole mettersi alla prova e scalare la classifica.

1. **Escape Room**: Partecipa a uno scenario interattivo a enigmi
2. **Quiz competitivo**: Si iscrive a un torneo di assessment
3. **Classifica**: Monitora la propria posizione nel leaderboard
4. **Badge**: Sblocca riconoscimenti per i risultati ottenuti
5. **Premi**: Riceve certificati per performance eccezionali

*Tempo stimato: continuo, in base alla partecipazione*
`
    },
    {
      id: 'features-overview',
      title: 'Panoramica delle Funzionalità',
      content: `
## Panoramica delle Funzionalità

La piattaforma è organizzata in moduli funzionali, ciascuno dedicato a un'area specifica:

### Moduli Principali

| Modulo | Descrizione | Destinatari |
|--------|-------------|-------------|
| **Dashboard** | Centro di controllo personale con widget informativi | Tutti gli utenti |
| **Forum** | Discussioni e community tematiche | Tutti gli utenti |
| **Corsi (LMS)** | Formazione con video, quiz e certificazioni | Tutti gli utenti |
| **Documenti** | Repository documentale con versioning | Tutti gli utenti |
| **Eventi** | Calendario, prenotazioni e webinar | Tutti gli utenti |
| **Task Force** | Gruppi di lavoro con Kanban e chat | Membri invitati |
| **Compliance Clinic** | Consulenza con esperti qualificati | Tutti gli utenti |
| **Gamification** | Punti, badge, tornei e classifiche | Tutti gli utenti |

### Moduli Amministrativi

| Modulo | Descrizione | Destinatari |
|--------|-------------|-------------|
| **Gestione Utenti** | Approvazione, ruoli e permessi | Admin |
| **Gestione Contenuti** | Corsi, documenti, forum | Admin |
| **Gestione Eventi** | Calendario e prenotazioni | Admin |
| **Newsletter** | Comunicazioni e campagne email | Admin |
| **Impostazioni** | Configurazione sistema e tema | Super Admin |
`
    },
    {
      id: 'system-requirements',
      title: 'Requisiti di Sistema',
      content: `
## Requisiti di Sistema

### Browser Supportati

La piattaforma è compatibile con le versioni recenti dei principali browser:

| Browser | Versione Minima | Note |
|---------|-----------------|------|
| **Google Chrome** | 90+ | Consigliato |
| **Mozilla Firefox** | 88+ | Pienamente supportato |
| **Microsoft Edge** | 90+ | Pienamente supportato |
| **Apple Safari** | 14+ | Pienamente supportato |

> **Nota**: Si consiglia di mantenere sempre aggiornato il browser per garantire sicurezza e compatibilità ottimale.

### Requisiti di Connessione

| Attività | Banda Minima | Banda Consigliata |
|----------|--------------|-------------------|
| Navigazione generale | 1 Mbps | 5 Mbps |
| Video lezioni | 2 Mbps | 10 Mbps |
| Videoconferenze (Jitsi) | 3 Mbps | 15 Mbps |
| Download documenti | 1 Mbps | 10 Mbps |

### Dispositivi Mobile

La piattaforma è **completamente responsive** e utilizzabile da:

- **Smartphone**: iOS 14+ / Android 10+
- **Tablet**: iPad / Tablet Android

Alcune funzionalità avanzate (es. Kanban drag-drop) sono ottimizzate per schermi più ampi.

### Requisiti per Funzionalità Specifiche

| Funzionalità | Requisito |
|--------------|-----------|
| **2FA (Autenticazione a due fattori)** | App autenticatore (Google Authenticator, Authy, Microsoft Authenticator) |
| **Download certificati** | Lettore PDF |
| **Videoconferenze** | Webcam e microfono (opzionali) |
| **Upload documenti** | Max 10 MB per file |
`
    }
  ]
}
